import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, exhaustMap, filter, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';

import { AddQuillTemplateGQL, UpdateQuillTemplateGQL, DeleteQuillTemplateGQL } from '../../../generated/graphql.generated';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

import { brandingFeature } from '../../state/branding.store';
import { MutateTemplateComponent } from '../mutate-template/mutate-template.component';

import { TemplatesActions , templatesFeature } from './templates.store';


export const addTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const addQuillTemplatesGQL = inject(AddQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateCreationRequested),
    mergeMap(({ input, requestId }) =>
      addQuillTemplatesGQL.mutate({input}).pipe(
        map(({ data }) => {
          if (data?.addQuillTemplate?.template) {
            notify.success('Template added successfully.');
            return TemplatesActions.templateCreated({ template: data.addQuillTemplate.template, requestId });
          }
          const error = new Error('No template returned');
          notify.error('Failed to add template.', error.message);
          return TemplatesActions.templateCreationFailed({ error, requestId });
        }),
        catchError((error) => {
          notify.apolloError('Failed to add template.', error);
          return of(TemplatesActions.templateCreationFailed({ error, requestId }));
        })
      )
    )
  );
}, { functional: true });

export const updateTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const updateQuillTemplatesGQL = inject(UpdateQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateUpdateRequested),
    mergeMap(({ input, requestId }) =>
      updateQuillTemplatesGQL.mutate({input}).pipe(
        map(({ data }) => {
          if (data?.updateQuillTemplate?.templates?.length) {
            notify.success('Template updated successfully.');
            return TemplatesActions.templateUpdated({ template: data.updateQuillTemplate.templates[0], requestId });
          }
          const error = new Error('No template returned');
          notify.error('Failed to update template.', error.message);
          return TemplatesActions.templateUpdateFailed({ error, requestId });
        }),
        catchError((error) => {
          notify.apolloError('Failed to update template.', error);
          return of(TemplatesActions.templateUpdateFailed({ error, requestId }));
        })
      )
    )
  );
}, { functional: true });

export const deleteTemplate$ = createEffect(() => {
  const actions$ = inject(Actions);
  const deleteQuillTemplatesGQL = inject(DeleteQuillTemplateGQL);
  const notify = inject(FreyaNotificationsService);
  return actions$.pipe(
    ofType(TemplatesActions.templateDeletionRequested),
    mergeMap(({ id }) =>
      deleteQuillTemplatesGQL.mutate({ input: { ids: [id] } }).pipe(
        map(({ data }) => {
          if (data?.deleteQuillTemplate) {
            notify.success('Template deleted successfully.');
            return TemplatesActions.templateDeleted({ id });
          }
          return TemplatesActions.templateDeletionFailed({ error: new Error('No template deleted') });
        }),
        catchError((error) => {
          notify.apolloError('Failed to delete template.', error);
          return of(TemplatesActions.templateDeletionFailed({ error }));
        })
      )
    )
  );
}, { functional: true });

/**
 * Opens the dialog and nothing more. The dialog dispatches its own save and
 * closes itself once the mutation succeeds, so that a rejected save leaves the
 * form — and everything typed into it — on screen.
 */
export const openAddTemplateDialog$ = createEffect(() => {
  const actions$ = inject(Actions);
  const dialogService = inject(DialogService);
  return actions$.pipe(
    ofType(TemplatesActions.createTemplateButtonClicked),
    tap(({location}) => {
      dialogService.open(MutateTemplateComponent, {
        header: 'Add New Template',
        width: '50%',
        // Only seed a location when there is one: an absent location must leave
        // the dialog's own defaults intact rather than blank them out.
        data: { template: location ? { location } : {} },
      });
    })
  );
}, { functional: true, dispatch: false });

export const openEditTemplateDialog$ = createEffect(() => {
  const actions$ = inject(Actions);
  const dialogService = inject(DialogService);
  const store = inject(Store);
  return actions$.pipe(
    ofType(TemplatesActions.editTemplateButtonClicked),
    withLatestFrom(store.select(brandingFeature.selectTemplates)),
    tap(([{ template, id }, templates]) => {
      let templateToEdit = template;

      if (!templateToEdit && id) {
        // If template is not provided, find it by ID
        // While getting template from store, ensure it is parsed correctly
        const matchingTemplate = templates.find(t => t.id === id);
        templateToEdit = {
            ...matchingTemplate,
            contents: JSON.parse(matchingTemplate?.contents || '{}'),
            subjectContents: matchingTemplate?.subjectContents ? JSON.parse(matchingTemplate.subjectContents) : null,
        }
      }

      dialogService.open(MutateTemplateComponent, {
        header: 'Edit Template',
        width: '50%',
        data: { template: templateToEdit},
      });
    })
  );
}, { functional: true, dispatch: false });

export const saveTemplateEdits$ = createEffect(() => {
    const actions$ = inject(Actions);
    const store = inject(Store);

    return actions$.pipe(
        ofType(TemplatesActions.saveButtonClicked),
        withLatestFrom(store.select(templatesFeature.selectTemplateContentEdits)),
        filter(([{template}, templateContentEdits]) =>  Boolean(templateContentEdits[template.id])),
        exhaustMap(([{ template }, templateContentEdits]) => {
            const {contents, subjectContents} = templateContentEdits[template.id];

            return of(TemplatesActions.templateUpdateRequested({
                input: {
                    ids: [template.id],
                    update: {
                        location: template.location,
                        ...(contents && {contents: JSON.stringify(contents)}),
                        ...(subjectContents && {subjectContents: JSON.stringify(subjectContents)})
                    }
                }
            }));
        })
    );
}, { functional: true });
